export const SAT_QUESTION_FOCUS_SKILLS = [
  // === MATH ===

  // Heart of Algebra
  'Linear Equations and Inequalities',
  'Linear Functions',
  'Systems of Linear Equations',
  // Passport to Advanced Mathematics
  'Quadratic Equations',
  'Radical and Exponential Expressions and Equations',
  'Polynomial Operations, Factors and Graphs',
  'Operations on Rational Expressions',
  'Nonlinear Equation Graphs',
  'Expression Structure',
  'Isolating Quantities',
  'Function Notation',
  // Problem Solving and Data Analysis
  'Ratios, Rates, Proportions, and Percents',
  'Unit Conversion',
  'Interpreting Tables and Graphs',
  'Linear and Exponential Growth',
  'Data Inferences',
  'Center, Spread, and Shapes of Distribution',
  'Collecting Data and Concluding',
  // Additional Topics in Mathematics
  'Areas and Volumes',
  'Right Triangle Rules and Manipulation',
  'Angles, Arc Length, and Trig Functions',
  'Circle Theorems and Equations',
  'Complex Numbers',

  // === READING ===

  'Literature',
  'Social Science',
  'Science',
  'History',
  'Paired Passage',

  // === WRITING ===

  // Argument
  'Setting Up Ideas',
  'Strong Support',
  // Informative
  'Relevant Information',
  'Sequencing Sentences',
  // Effective Language Use
  'Precision and Concision',
  'Style and Tone',
  'Combining Sentences',
  // Grammar
  'End-of-sentence Punctuation',
  'Within-sentence Punctuation',
  'Sentence Boundaries',
  'Subordination and Coordination',
  'Parallel Structure',
  'Modifier Placement',
  'Verb Tense and Mood',
  'Pronoun Clarity and Agreement',
  'Possessive Determiners',
  'Subject Verb Agreement',
  'Noun Agreement',
  'Frequently Confused Words',
  'Conventional Expression',
  'Logical Comparison',
  'Possessive Nouns and Pronouns',
  'Items in Series',
  'Writing with Data',
  'Nonessential Elements',
  'Introductions and Conclusions',

  // DSAT MATH
  'Linear equations in one variable',
  'Linear functions',
  'Linear equations in two variables',
  'Systems of two linear equations in two variables',
  'Linear inequalities in one or two variables',
  'Equivalent expressions',
  'Nonlinear equations in one variable and systems of equations in two variables',
  'Nonlinear functions',
  'Ratios, rates, proportional relationships, and units',
  'Percentages',
  'One-variable data: Distributions and measures of center and spread',
  'Two-variable data: Models and scatterplots',
  'Probability and conditional probability',
  'Inference from sample statistics and margin of error',
  'Area and volume',
  'Lines, angles, and triangles',
  'Right triangles and trigonometry',
  'Circles',
  'Evaluating statistical claims: Observational studies and experiments',

  // DSAT READING WRITING
  'Inferences',
  'Central Ideas and Details',
  'Cross-Text Connections',
  'Text Structure and Purpose',
  'Transitions',
  'Command of Evidence',
  'Boundaries',
  'Form, Structure, and Sense',
  'Rhetorical Synthesis',
  'Words in Context',
] as const

export const MATH_DIGITAL_SAT_DOMAINS = [
  'Algebra',
  'Problem-Solving and Data Analysis',
  'Advanced Math',
  'Geometry and Trigonometry',
] as const

export const READING_DIGITAL_SAT_DOMAINS = [
  'Information and Ideas',
  'Craft and Structure',
  'Expression of Ideas',
  'Standard English Conventions',
] as const

export const DIGITAL_SAT_DOMAINS = [
  ...MATH_DIGITAL_SAT_DOMAINS,
  ...READING_DIGITAL_SAT_DOMAINS,
] as const

export type DigitalSATDomain = (typeof DIGITAL_SAT_DOMAINS)[number]

export const DIGITAL_SAT_DOMAIN_TO_FOCUS_SKILLS: Record<DigitalSATDomain, SATQuestionFocusSkill[]> =
  {
    Algebra: [
      'Linear equations in one variable',
      'Linear functions',
      'Linear equations in two variables',
      'Systems of two linear equations in two variables',
      'Linear inequalities in one or two variables',
    ],
    'Problem-Solving and Data Analysis': [
      'Ratios, rates, proportional relationships, and units',
      'Percentages',
      'One-variable data: Distributions and measures of center and spread',
      'Two-variable data: Models and scatterplots',
      'Probability and conditional probability',
      'Inference from sample statistics and margin of error',
      'Evaluating statistical claims: Observational studies and experiments',
    ],
    'Advanced Math': [
      'Nonlinear functions',
      'Nonlinear equations in one variable and systems of equations in two variables',
      'Equivalent expressions',
    ],
    'Geometry and Trigonometry': [
      'Area and volume',
      'Lines, angles, and triangles',
      'Right triangles and trigonometry',
      'Circles',
    ],
    'Information and Ideas': ['Central Ideas and Details', 'Inferences', 'Command of Evidence'],
    'Craft and Structure': [
      'Words in Context',
      'Text Structure and Purpose',
      'Cross-Text Connections',
    ],
    'Expression of Ideas': ['Rhetorical Synthesis', 'Transitions'],
    'Standard English Conventions': ['Boundaries', 'Form, Structure, and Sense'],
  }

export type SATQuestionFocusSkill = (typeof SAT_QUESTION_FOCUS_SKILLS)[number]

export type SATQuestionDifficulty = 'easy' | 'medium' | 'hard'

export type SATSuggestedPractice = {
  solutions_url?: string
  questions_url?: string
  ka_url: string
}

export const SAT_QUESTION_FOCUS_SKILL_TO_SUGGESTED_PRACTICE: Record<
  SATQuestionFocusSkill,
  SATSuggestedPractice
> = {
  // DSAT MATH
  'Linear equations in one variable': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:algebra-medium/x0fcc98a58ba3bea7:solving-linear-equations-and-linear-inequalities-medium/a/v2-sat-lesson-solving-linear-equations-and-inequalities',
  },
  'Linear functions': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:algebra-medium/x0fcc98a58ba3bea7:linear-relationship-word-problems-medium/a/v2-sat-lesson-understanding-linear-relationships',
  },
  'Linear equations in two variables': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:algebra-medium/x0fcc98a58ba3bea7:solving-linear-equations-and-linear-inequalities-medium/a/v2-sat-lesson-solving-linear-equations-and-inequalities',
  },
  'Systems of two linear equations in two variables': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:algebra-medium/x0fcc98a58ba3bea7:solving-systems-of-linear-equations-medium/a/v2-sat-lesson-solving-systems-of-linear-equations',
  },
  'Linear inequalities in one or two variables': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:algebra-medium/x0fcc98a58ba3bea7:linear-inequality-word-problems-medium/a/v2-sat-lesson-linear-inequality-word-problems',
  },
  'Equivalent expressions': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:advanced-math-medium/x0fcc98a58ba3bea7:operations-with-polynomials-medium/a/v2-sat-lesson-operations-with-polynomials',
  },
  'Nonlinear equations in one variable and systems of equations in two variables': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:advanced-math-medium/x0fcc98a58ba3bea7:linear-and-quadratic-systems-medium/a/v2-sat-lesson-linear-and-quadratic-systems',
  },
  'Nonlinear functions': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:advanced-math-medium/x0fcc98a58ba3bea7:nonlinear-functions-medium/a/v2-sat-lesson-nonlinear-functions',
  },
  'Ratios, rates, proportional relationships, and units': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:problem-solving-and-data-analysis-medium/x0fcc98a58ba3bea7:ratios-rates-and-proportions-medium/a/v2-sat-lesson-ratios-rates-and-proportions',
  },
  Percentages: {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:problem-solving-and-data-analysis-medium/x0fcc98a58ba3bea7:percentages-medium/a/v2-sat-lesson-percentages',
  },
  'Evaluating statistical claims: Observational studies and experiments': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:problem-solving-and-data-analysis-medium/x0fcc98a58ba3bea7:evaluating-statistical-claims-medium/a/v2-sat-lesson-evaluating-statistical-claims',
  },
  'One-variable data: Distributions and measures of center and spread': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:problem-solving-and-data-analysis-medium/x0fcc98a58ba3bea7:center-spread-and-shape-of-distributions-medium/a/v2-sat-lesson-center-spread-and-shape-of-distributions',
  },
  'Two-variable data: Models and scatterplots': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:problem-solving-and-data-analysis-medium/x0fcc98a58ba3bea7:scatterplots-medium/a/v2-sat-lesson-scatterplots',
  },
  'Probability and conditional probability': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:problem-solving-and-data-analysis-medium/x0fcc98a58ba3bea7:probability-and-relative-frequency-medium/a/v2-sat-lesson-probability-and-relative-frequency',
  },
  'Inference from sample statistics and margin of error': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:problem-solving-and-data-analysis-medium/x0fcc98a58ba3bea7:data-inferences-medium/a/v2-sat-lesson-data-inferences',
  },
  'Area and volume': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:geometry-and-trigonometry-medium/x0fcc98a58ba3bea7:area-and-volume-medium/a/v2-sat-lesson-area-and-volume',
  },
  'Lines, angles, and triangles': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:geometry-and-trigonometry-medium/x0fcc98a58ba3bea7:congruence-similarity-and-angle-relationships-medium/a/v2-sat-lesson-congruence-similarity-and-angle-relationships',
  },
  'Right triangles and trigonometry': {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:geometry-and-trigonometry-medium/x0fcc98a58ba3bea7:right-triangle-trigonometry-medium/a/v2-sat-lesson-right-triangle-trigonometry',
  },
  Circles: {
    ka_url:
      'https://www.khanacademy.org/test-prep/v2-sat-math/x0fcc98a58ba3bea7:geometry-and-trigonometry-medium/x0fcc98a58ba3bea7:circle-theorems-medium/a/v2-sat-lesson-circle-theorems',
  },
  // DSAT READING
  Inferences: {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:inferences-2/a/inferences-lesson',
  },
  'Central Ideas and Details': {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:central-ideas-and-details-2/a/central-ideas-and-details-lesson',
  },
  'Cross-Text Connections': {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:cross-text-connections-2/a/cross-text-connections-lesson',
  },
  'Text Structure and Purpose': {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:text-structure-and-purpose-2/a/text-structure-and-purpose-lesson',
  },
  Transitions: {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:transitions-2/a/transitions-lesson',
  },
  'Command of Evidence': {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:command-of-evidence-textual-2/a/command-of-evidence-textual-lesson',
  },
  Boundaries: {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:boundaries-2/a/boundaries-overview',
  },
  'Form, Structure, and Sense': {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:form-structure-and-sense-2/a/form-structure-and-sense-overview',
  },
  'Rhetorical Synthesis': {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:rhetorical-synthesis-2/a/rhetorical-synthesis-lesson',
  },
  'Words in Context': {
    ka_url:
      'https://www.khanacademy.org/test-prep/sat-reading-and-writing/x0d47bcec73eb6c4b:medium/x0d47bcec73eb6c4b:words-in-context-2/a/words-in-context-lesson',
  },
  // MATH
  'Linear Equations and Inequalities': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.gldnt4vec5su',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.ey1mn3p6xz2f',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:heart-of-algebra-lessons-by-skill/a/gtp--sat-math--article--solving-linear-equations-and-linear-inequalities--lesson?modal=1',
  },
  'Linear Functions': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.7ufbbnsph6yq',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.fhojxve6qln',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:heart-of-algebra-lessons-by-skill/a/gtp--sat-math--article--understanding-linear-relationships--lesson?modal=1',
  },
  'Systems of Linear Equations': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.muppqu6daxz7',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.jzjedxya8cjy',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:heart-of-algebra-lessons-by-skill/a/gtp--sat-math--article--solving-system-of-linear-equations--lesson?modal=1',
  },
  'Quadratic Equations': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.ili9w8n35dom',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.k6yabvt5hmob',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--solving-quadratic-equations--lesson?modal=1',
  },
  'Radical and Exponential Expressions and Equations': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.ut7d52kd7ckx',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.mfdig1a94oqj',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--radical-and-rational-equations--lesson?modal=1',
  },
  'Polynomial Operations, Factors and Graphs': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.3j960bibm0bv',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.xr6p90tocx8z',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--polynomial-factors-and-graphs--lesson?modal=1',
  },
  'Operations on Rational Expressions': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.q2xmzh4kxzwk',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.8x9asa6icg58',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--operations-with-rational-expressions--lesson?modal=1',
  },
  'Nonlinear Equation Graphs': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.bhg6bnwmtuus',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.tmqrefnhiwc',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--polynomial-factors-and-graphs--lesson?modal=1',
  },
  'Expression Structure': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.3ztnbr24yxt3',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.36culz3y2rsk',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--structure-in-expressions--lesson?modal=1',
  },
  'Isolating Quantities': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.3t1ftoatcv4m',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.tlgomnvgsbf2',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--isolating-quantities--lesson?modal=1',
  },
  'Function Notation': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.l5qtcpafu473',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.y3vaf4syf2zh',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:passport-to-advanced-math-lessons-by-skill/a/gtp--sat-math--article--function-notation--lesson?modal=1',
  },
  'Ratios, Rates, Proportions, and Percents': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.xkn7f7m1toqm',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.nzpkby3sgfhm',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:problem-solving-and-data-analysis-lessons-by-skill/a/gtp--sat-math--article--ratios-rates-and-proportions--lesson?modal=1',
  },
  'Unit Conversion': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.ub6s8fh4wrzf',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.ynex9cmmsre9',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:problem-solving-and-data-analysis-lessons-by-skill/a/gtp--sat-math--article--units--lesson?modal=1',
  },
  'Interpreting Tables and Graphs': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.jo0ywcqizd96',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.5129wsgw0s4c',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:problem-solving-and-data-analysis-lessons-by-skill/a/gtp--sat-math--article--table-data--lesson?modal=1',
  },
  'Linear and Exponential Growth': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.hgh0mxo5afw7',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.9i3oi7m4pxrm',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:problem-solving-and-data-analysis-lessons-by-skill/a/gtp--sat-math--article--linear-and-exponential-growth--lesson?modal=1',
  },
  'Data Inferences': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.gbabb9bsnaj2',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.v8j1wfbzvjw',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:problem-solving-and-data-analysis-lessons-by-skill/a/gtp--sat-math--article--data-inferences--lesson?modal=1',
  },
  'Center, Spread, and Shapes of Distribution': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.f39sj4s1hqri',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.7hqtx1tif0rq',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:problem-solving-and-data-analysis-lessons-by-skill/a/gtp--sat-math--article--center-spread-and-shape-of-distributions--lesson?modal=1',
  },
  'Collecting Data and Concluding': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.qit6n1d5ulcp',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.i2kgz384f15g',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:problem-solving-and-data-analysis-lessons-by-skill/a/gtp--sat-math--article--data-collection-and-conclusions--lesson?modal=1',
  },
  'Areas and Volumes': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.kumcah6i2qyl',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.khv7qey1fom4',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:additional-math-topics-lessons-by-skill/a/gtp--sat-math--article--volume-word-problems--lesson?modal=1',
  },
  'Right Triangle Rules and Manipulation': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.efdb98bc30k9',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.746de5nq3t74',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:additional-math-topics-lessons-by-skill/a/gtp--sat-math--article--right-triangle-trigonometry--lesson?modal=1',
  },
  'Angles, Arc Length, and Trig Functions': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.ufxv6j5pxiik',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.9ikyhw4zthkq',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:additional-math-topics-lessons-by-skill/a/gtp--sat-math--article--angles-arc-lengths-and-trig-functions--lesson?modal=1',
  },
  'Circle Theorems and Equations': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.ylz6e7z6y380',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.323z8orpc3i',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:additional-math-topics-lessons-by-skill/a/gtp--sat-math--article--circle-theorems--lesson?modal=1',
  },
  'Complex Numbers': {
    solutions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.f9d1zdn6x7fh',
    questions_url:
      'https://docs.google.com/document/d/1hbTvPz2xFXuQnWzQyKHUDBJ6dHoDaUslhw3PWzxFHZo/edit#bookmark=id.gj4wgk3go9p',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-652/x0a8c2e5f:additional-math-topics-lessons-by-skill/a/gtp--sat-math--article--complex-numbers--lesson?modal=1',
  },

  Literature: {
    solutions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.cybt1dotl3k',
    questions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.cxfwilyjydha',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-494/x0a8c2e5f:full-passage-walkthroughs/a/how-to-approach-a-literature-passage?modal=1',
  },
  'Social Science': {
    solutions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.wbjinhq9rmz',
    questions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.kztwxss9sfd8',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-494/x0a8c2e5f:full-passage-walkthroughs/a/how-to-approach-a-social-science-passage?modal=1',
  },
  Science: {
    solutions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.w6iiupveg7xk',
    questions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.kmjxknh8tmt3',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-494/x0a8c2e5f:full-passage-walkthroughs/a/how-to-approach-a-science-passage?modal=1',
  },
  History: {
    solutions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#heading=h.gzdqnlm30hg2',
    questions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.3iyhg1x8jrj2',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-494/x0a8c2e5f:full-passage-walkthroughs/a/how-to-approach-a-history-passage?modal=1',
  },
  'Paired Passage': {
    solutions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#bookmark=id.ccwqnruey0xp',
    questions_url:
      'https://docs.google.com/document/d/104S7ZHs6XMKIbLq3dutwC9G1IZk9HR6ke21Q8B63jlk/edit#heading=h.51wuu8ls04y3',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:untitled-494/x0a8c2e5f:reading-test-strategies-and-question-guides/a/sat-reading--article--paired-passages--quick-guide?modal=1',
  },

  // WRITING

  'Setting Up Ideas': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.a08x6v3bgpxe',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.928o4uemykrz',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/v/writing-setting-up-ideas-video-lesson?modal=1',
  },
  'Strong Support': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.eypvm2ay5kn6',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.i5x7tghxhxyf',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--strong-support--quick-guide?modal=1',
  },
  'Relevant Information': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.6fjwj9649dxe',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.kqr4dkldpd',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--relevant-information--quick-guide?modal=1',
  },
  'Sequencing Sentences': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.icc260m1e9so',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.9kq6cu111h0o',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--sequencing-sentences--quick-guide?modal=1',
  },
  'Precision and Concision': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.3xcvaw4qstlb',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.uojxgg1fzf9p',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--precise-word-choice--quick-guide?modal=1',
  },
  'Style and Tone': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.1qds98gcslde',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.x0umo7xq31s8',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--formal-vs-casual-language--quick-guide?modal=1',
  },
  'Combining Sentences': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.vmept9x2w75',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.f4pyj2blrz0g',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/v/writing-combining-sentences-video-lesson?modal=1',
  },
  'End-of-sentence Punctuation': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.72jwulxyj3si',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.yim8jc4o8d4p',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--linking-clauses--quick-guide?modal=1',
  },
  'Within-sentence Punctuation': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.dxt1nd3t2hqb',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.m9i0p3rajuno',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--commas--quick-guide?modal=1',
  },
  'Sentence Boundaries': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.szitapaivppl',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.bh5zygh5kq5q',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--linking-clauses--quick-guide?modal=1',
  },
  'Subordination and Coordination': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.31e4tsrvy2dx',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.1n653wtlgizr',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/v/writing-combining-sentences-video-lesson?modal=1',
  },
  'Parallel Structure': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.uxe0e4prjx3z',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.2f01wx27hj07',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--parallel-structure--quick-guide?modal=1',
  },
  'Modifier Placement': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.i60bkr704jn7',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.d79mwvxmdb75',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--modifier-placement--quick-guide?modal=1',
  },
  'Verb Tense and Mood': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.ytd9h02uo84f',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.5f8gl0l3r2dq',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--verb-tense-and-mood--quick-guide?modal=1',
  },
  'Pronoun Clarity and Agreement': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.a32c2z1n8lj',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.3qyj16ptgbhk',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--pronoun-clarity--quick-guide?modal=1',
  },
  'Possessive Determiners': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.rwbr9522wdw',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.39us3sc5x358',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--possessive-nouns--quick-guide?modal=1',
  },
  'Subject Verb Agreement': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.pldl9jo3ilqa',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.lrelyar10z0w',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--subject-verb-agreement--quick-guide?modal=1',
  },
  'Noun Agreement': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.344znhqpfkxl',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.h0n68qppxolf',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--noun-agreement--quick-guide?modal=1',
  },
  'Frequently Confused Words': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.azgd47r63apq',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.lq4co18e1z4h',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--frequently-confused-words--quick-guide?modal=1',
  },
  'Conventional Expression': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.azgd47r63apq',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.yotb8k1dy6qa',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--conventional-expressions--quick-guide?modal=1',
  },
  'Logical Comparison': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.4962eg1575mn',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.rqg5e1yodpal',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--logical-comparison--quick-guide?modal=1',
  },
  'Possessive Nouns and Pronouns': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.au72d8w0fnjs',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.lhdrdmd0ovpv',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--possessive-nouns--quick-guide?modal=1',
  },
  'Items in Series': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.pbvj34bynrum',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.tvr3amobwjmz',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--list-punctuation--quick-guide?modal=1',
  },
  'Writing with Data': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.mtf1a27j6zrp',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.vnvnqalwcvtw',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--graphs--quick-guide?modal=1',
  },
  'Nonessential Elements': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.unj38w1vlwik',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.4pcppi9eb8s1',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:writing-and-language-test-strategies-and-question-guides/a/sat-writing--article--nonessential-elements--quick-guide?modal=1',
  },
  // This was overriden by the "Transitions" digital SAT topic
  // Transitions: {
  //   solutions_url:
  //     'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.qwj6geuscnxg',
  //   questions_url:
  //     'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.bd904leikdt',
  //   ka_url:
  //     'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--transition-words--quick-guide?modal=1',
  // },
  'Introductions and Conclusions': {
    solutions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.fs18q6pp9hg9',
    questions_url:
      'https://docs.google.com/document/d/1DdY44AiJMtCvP92A9WCcPBSGCBubpMI2xUYDxbcStCI/edit#bookmark=id.vn46uy84gh5v',
    ka_url:
      'https://www.khanacademy.org/test-prep/sat/x0a8c2e5f:about-the-sat/x0a8c2e5f:grammar-strategies-and-question-guides/a/sat-writing--article--introductions-and-conclusions--quick-guide?modal=1',
  },
}
